import React, { useRef, useEffect, useState } from 'react';
import { HEADER_TAGLINE, PRIMARY_CTA_TEXT, SITE_NAME, DOMAIN } from '@/config';
import Reveal from '@/components/ui/Reveal';
import { useRedirect } from '@/context/RedirectContext';
import { useScroll } from '@/context/ScrollContext';
import { useSettings } from '@/context/SettingsContext';
import { ArrowRightIcon } from '@/components/ui/Icons';

interface HeaderProps {
    id: string;
}

const Header = React.memo(React.forwardRef<HTMLElement, HeaderProps>(({ id }, ref) => {
    const { redirect } = useRedirect();
    const { scrollToSection } = useScroll();
    const { isMagneticLogoEnabled } = useSettings();
    const logoContainerRef = useRef<HTMLDivElement>(null);
    const [isHovering, setIsHovering] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    // Cache letter positions to avoid getBoundingClientRect on every mouse/touch move (layout thrashing)
    const letterPositionsRef = useRef<{ el: HTMLElement, centerX: number, centerY: number }[]>([]);

    const cacheLetterPositions = () => {
        const container = logoContainerRef.current;
        if (container) {
            const letters = container.querySelectorAll('.animated-logo-text span');
            letterPositionsRef.current = Array.from(letters)
                .filter((l): l is HTMLElement => l instanceof HTMLElement)
                .map(el => {
                    const rect = el.getBoundingClientRect();
                    return {
                        el,
                        centerX: rect.left + rect.width / 2,
                        centerY: rect.top + rect.height / 2,
                    };
                });
        }
    };

    useEffect(() => {
        const container = logoContainerRef.current;
        if (!container || !isHovering || !isMagneticLogoEnabled) return;

        const maxDistance = 80;
        const pushStrength = 25;

        // Use cached positions instead of calling getBoundingClientRect per letter per frame
        letterPositionsRef.current.forEach(({ el, centerX, centerY }) => {
            const deltaX = centerX - pos.x;
            const deltaY = centerY - pos.y;
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (distance < maxDistance) {
                const force = 1 - (distance / maxDistance);
                const moveX = (deltaX / distance) * force * pushStrength;
                const moveY = (deltaY / distance) * force * pushStrength;
                el.style.transform = `translate(${moveX}px, ${moveY}px)`;
            } else {
                el.style.transform = 'translate(0, 0)';
            }
        });
    }, [pos.x, pos.y, isHovering, isMagneticLogoEnabled]);

    const handleMouseEnter = () => {
        if (isMagneticLogoEnabled) {
            setIsHovering(true);
            cacheLetterPositions();
        }
    };

    const handleMouseMoveLocal = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isMagneticLogoEnabled || !isHovering) return;
        setPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseLeave = () => {
        if (isMagneticLogoEnabled) {
            resetMagneticLetters();
        }
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (isMagneticLogoEnabled) {
            setIsHovering(true);
            cacheLetterPositions();
            const touch = e.touches[0];
            if (touch) {
                setPos({ x: touch.clientX, y: touch.clientY });
            }
        }
    };

    const handleTouchMoveLocal = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!isMagneticLogoEnabled || !isHovering) return;
        // Prevent pull-to-refresh or scrolling while interacting with the logo
        if (e.cancelable) {
            e.preventDefault();
        }
        const touch = e.touches[0];
        if (touch) {
            setPos({ x: touch.clientX, y: touch.clientY });
        }
    };

    const handleTouchEnd = () => {
        if (isMagneticLogoEnabled) {
            resetMagneticLetters();
        }
    };

    const resetMagneticLetters = () => {
        setIsHovering(false);
        const container = logoContainerRef.current;
        if (!container) return;
        const letters = container.querySelectorAll('.animated-logo-text span');
        letters.forEach(letter => {
            if (letter instanceof HTMLElement) {
                letter.style.transform = 'translate(0, 0)';
            }
        });
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault();
        const siteName = e.currentTarget.getAttribute('data-site');
        const href = e.currentTarget.getAttribute('data-href');
        if (siteName && href) {
            redirect(siteName, href);
        }
    };

    const logoText = SITE_NAME;

    return (
        <Reveal as="header" ref={ref} id={id} className="flex flex-col items-center justify-center pt-20 md:pt-32 pb-10 mb-12 relative">
            <div
                ref={logoContainerRef}
                className="logo-container flex items-center justify-center mb-6 select-none"
                style={{
                    '--delay': '100ms',
                    touchAction: 'none'
                } as React.CSSProperties}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMoveLocal}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMoveLocal}
                onTouchEnd={handleTouchEnd}
                suppressHydrationWarning
            >
                <h1 className="animated-logo-text text-4xl sm:text-5xl md:text-7xl" data-text={logoText}>
                    {logoText.split('').map((char, index) => (
                        <span key={index}>{char}</span>
                    ))}
                </h1>
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-slate-300 text-center mb-8 md:mb-12 max-w-2xl" style={{ '--delay': '200ms' } as React.CSSProperties}>
                {HEADER_TAGLINE.split(SITE_NAME).reduce((acc, part, index) => {
                    if (index === 0) return [...acc, part];
                    return [...acc, <span key={index} className="text-orange-500 font-semibold">{SITE_NAME}</span>, part];
                }, [] as React.ReactNode[])}
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6" style={{ '--delay': '300ms' } as React.CSSProperties}>
                <a href={`https://${DOMAIN}`} target="_blank" rel="noopener noreferrer" data-href={`https://${DOMAIN}`} className="cta-button pulse text-white font-bold py-4 sm:py-5 px-8 sm:px-12 rounded-full text-lg sm:text-xl md:text-2xl inline-flex items-center" data-site={SITE_NAME} onClick={handleLinkClick}>
                    <span>{PRIMARY_CTA_TEXT}</span>
                    <ArrowRightIcon className="h-5 w-5 sm:h-7 sm:w-7 ml-2 sm:ml-3" />
                </a>
                <button onClick={() => scrollToSection('about', { behavior: 'smooth', block: 'start' })} className="learn-more-btn font-semibold text-orange-400 border-2 border-orange-500 dark:border-orange-400/50 rounded-full py-4 sm:py-5 px-8 sm:px-12 text-lg sm:text-xl md:text-2xl transition-all duration-300 hover:bg-orange-400/10 hover:border-orange-600 dark:hover:border-orange-400 hover:shadow-lg hover:shadow-orange-500/20 transform hover:-translate-y-1">
                    Learn More
                </button>
            </div>
        </Reveal>
    );
}));

Header.displayName = 'Header';

export default Header;